import { useMutation } from '@apollo/client'
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  IconButton
} from '@material-ui/core'
import {
  Close as CloseIcon,
  InsertDriveFile as FileIcon
} from '@material-ui/icons'
import React, { useRef, useState } from 'react'
import { toast } from 'react-toastify'

import {
  DELETE_PAYMENT_RECEIPT_MUTATION,
  UPLOAD_PAYMENT_RECEIPT_MUTATION
} from '../../graphql/uploadPerEntity'
import { getTranslate } from '../../localization'
import useStyles from './styles/PaymentMethod.styles'

// `strict` puts catch variables at `unknown`, so the Apollo error's message has to be
// reached through a narrowing rather than assumed. Falls back to the stringified value so an
// unexpected throw still produces a visible message instead of an empty toast.
const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error)

interface UploadBoxProps {
  disabled?: boolean
  entityID: string
  entityCode: string
  onFileUpload?: (file: File) => void
  attachments: string[]
  setattachments: React.Dispatch<React.SetStateAction<string[]>>
}

interface UploadedFileInfo {
  file: File
  progress: number
  mediaID?: string
}

export const UploadBox: React.FC<UploadBoxProps> = ({
  disabled = false,
  entityID,
  entityCode,
  onFileUpload,
  attachments,
  setattachments
}) => {
  const classes = useStyles()
  const inputRef = useRef<HTMLInputElement>(null)
  const [files, setFiles] = useState<UploadedFileInfo[]>([])

  // Per-entity mutations: the generic uploadGolang/deleteImage cannot be authorized by IAM,
  // which keys on the root field name and cannot read arguments, so they are unmapped and
  // deprecated. See graphql/uploadPerEntity.ts.
  const [uploadFileMutation] = useMutation(UPLOAD_PAYMENT_RECEIPT_MUTATION)
  const [deleteImage] = useMutation(DELETE_PAYMENT_RECEIPT_MUTATION)

  const simulateProgress = (index: number) => {
    let progress = 0
    const interval = setInterval(() => {
      progress += 10
      setFiles((prev) =>
        prev.map((f, i) =>
          i === index ? { ...f, progress: Math.min(progress, 100) } : f
        )
      )
      if (progress >= 100) clearInterval(interval)
    }, 200)
  }

  const handleUpload = async (file: File) => {
    const fileIndex = files.length
    setFiles((prev) => [...prev, { file, progress: 0 }])
    simulateProgress(fileIndex)

    try {
      const { data } = await uploadFileMutation({
        variables: {
          file,
          entityID,
          entityCode
        },
        context: {
          hasUpload: true
        }
      })

      const mediaID = data.result.message
      setattachments([...attachments, mediaID])
      onFileUpload?.(file)

      setFiles((prev) =>
        prev.map((f, i) =>
          i === fileIndex ? { ...f, mediaID, progress: 100 } : f
        )
      )
    } catch (error) {
      // Both catches surface the failure. They previously only console.error'd, so a user
      // whose receipt upload failed saw the row sit there with no explanation — and a
      // payment receipt is the evidence a proforma is settled against, so silence here is
      // worse than elsewhere. ImageBox already toasts both outcomes; this matches it.
      toast.error(errorMessage(error))
    }
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files
    if (selectedFiles) {
      Array.from(selectedFiles).forEach(handleUpload)
    }
    if (inputRef.current) inputRef.current.value = '' // Reset input
  }

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    const droppedFiles = event.dataTransfer.files
    if (droppedFiles) {
      Array.from(droppedFiles).forEach(handleUpload)
    }
  }

  const preventDefaults = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleRemove = async (index: number) => {
    const fileInfo = files[index]
    if (!fileInfo?.mediaID) return

    try {
      await deleteImage({
        variables: {
          mediaID: fileInfo.mediaID,
          entityCode
        }
      })

      setFiles((prev) => prev.filter((_, i) => i !== index))
      setattachments((prev) => prev.filter((id) => id !== fileInfo.mediaID))
    } catch (err) {
      toast.error(errorMessage(err))
    }
  }

  return (
    <Paper
      elevation={0}
      className={classes.uploadBox}
      onDrop={handleDrop}
      onDragOver={preventDefaults}
      onDragEnter={preventDefaults}
      onDragLeave={preventDefaults}>
      <Box>
        <Typography variant="body2" align="center">
          {getTranslate('Drag & Drop Document to upload')}
        </Typography>
        <Typography variant="caption" align="center">
          (PDF, JPG, PNG)
        </Typography>
        <Box mt={1} display="flex" justifyContent="center">
          <Typography
            variant="body2"
            color="primary"
            style={{ cursor: disabled ? 'default' : 'pointer' }}
            onClick={() => !disabled && inputRef.current?.click()}>
            + {getTranslate('Upload from a Folder')}
          </Typography>
        </Box>
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          hidden
          multiple
          onChange={handleFileSelect}
          disabled={disabled}
        />
      </Box>

      {files.map((f, i) => (
        <Box key={i} mt={2} width="100%">
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center">
            <Box display="flex" alignItems="center">
              <FileIcon color="primary" />
              <Box className={classes.uploadedFileContainer}>
                <Typography variant="body2">{f.file.name}</Typography>
                <Typography variant="caption" color="primary">
                  {(f.file.size / (1024 * 1024)).toFixed(1)} MB
                </Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={() => handleRemove(i)}>
              <CloseIcon color="primary" />
            </IconButton>
          </Box>
          <Box mt={1}>
            <LinearProgress
              variant="determinate"
              value={f.progress}
              style={{ height: 6, borderRadius: 4 }}
              color="primary"
            />
            <Typography variant="caption" align="right" display="block">
              {f.progress}%
            </Typography>
          </Box>
        </Box>
      ))}
    </Paper>
  )
}
